/**
 * Reusable Only Games tabs.
 *
 * Markup contract (any page):
 *   <div data-og-tabs data-og-tabs-active="all">
 *     <div role="tablist">
 *       <button type="button" role="tab" data-og-tab="all" aria-selected="true">…</button>
 *       <button type="button" role="tab" data-og-tab="fantasy">…</button>
 *     </div>
 *     <!-- Optional panels: when present, tabs show/hide them -->
 *     <div data-og-tab-panel="all">…</div>
 *     <div data-og-tab-panel="fantasy" hidden>…</div>
 *   </div>
 *
 * Events (for non-panel / custom behaviour):
 *   root dispatches bubbling "og:tabs:change" with detail: { id, root, previousId }
 *
 * Programmatic:
 *   root.ogTabs.activate('fantasy')
 *   OnlyGamesTabs.init(root | document)
 */
(function (window, document) {
  'use strict';

  var EVENT_NAME = 'og:tabs:change';

  function getButtons(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-og-tab]'));
  }

  function getPanels(root) {
    return Array.prototype.slice.call(root.querySelectorAll('[data-og-tab-panel]'));
  }

  function activate(root, id, options) {
    options = options || {};
    var silent = options.silent === true;
    var buttons = getButtons(root);
    var panels = getPanels(root);
    var previousId = root.getAttribute('data-og-tabs-active');

    if (!id) return;
    if (previousId === id && !options.force) return;

    buttons.forEach(function (btn) {
      var on = btn.getAttribute('data-og-tab') === id;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
    });

    if (panels.length) {
      panels.forEach(function (panel) {
        var on = panel.getAttribute('data-og-tab-panel') === id;
        if (on) {
          panel.removeAttribute('hidden');
          panel.style.removeProperty('display');
        } else {
          panel.setAttribute('hidden', '');
        }
        panel.setAttribute('aria-hidden', on ? 'false' : 'true');
      });
    }

    root.setAttribute('data-og-tabs-active', id);

    if (!silent) {
      root.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          bubbles: true,
          detail: { id: id, previousId: previousId, root: root },
        })
      );
    }
  }

  function onKeydown(event, root, buttons) {
    var key = event.key;
    if (key !== 'ArrowLeft' && key !== 'ArrowRight' && key !== 'Home' && key !== 'End') {
      return;
    }

    var current = event.currentTarget;
    var index = buttons.indexOf(current);
    if (index < 0) return;

    event.preventDefault();

    var nextIndex = index;
    if (key === 'ArrowLeft') nextIndex = (index - 1 + buttons.length) % buttons.length;
    if (key === 'ArrowRight') nextIndex = (index + 1) % buttons.length;
    if (key === 'Home') nextIndex = 0;
    if (key === 'End') nextIndex = buttons.length - 1;

    var next = buttons[nextIndex];
    if (!next) return;
    next.focus();
    activate(root, next.getAttribute('data-og-tab'));
  }

  function initRoot(root) {
    if (!root || root.ogTabs) return root && root.ogTabs;

    var buttons = getButtons(root);
    if (!buttons.length) return null;

    buttons.forEach(function (btn) {
      if (!btn.getAttribute('type')) btn.setAttribute('type', 'button');
      if (!btn.getAttribute('role')) btn.setAttribute('role', 'tab');

      btn.addEventListener('click', function () {
        activate(root, btn.getAttribute('data-og-tab'));
      });
      btn.addEventListener('keydown', function (event) {
        onKeydown(event, root, buttons);
      });
    });

    var tablist = root.querySelector('[role="tablist"]');
    if (tablist && !tablist.getAttribute('aria-label') && !tablist.getAttribute('aria-labelledby')) {
      tablist.setAttribute('aria-label', 'Tabs');
    }

    var initial =
      root.getAttribute('data-og-tabs-active') ||
      (buttons.find(function (btn) {
        return btn.getAttribute('aria-selected') === 'true';
      }) ||
        buttons[0]
      ).getAttribute('data-og-tab');

    activate(root, initial, { silent: true, force: true });

    root.ogTabs = {
      activate: function (id) {
        activate(root, id);
      },
      getActive: function () {
        return root.getAttribute('data-og-tabs-active');
      },
    };

    return root.ogTabs;
  }

  function init(scope) {
    var context = scope || document;
    if (context.matches && context.matches('[data-og-tabs]')) {
      initRoot(context);
      return;
    }
    Array.prototype.forEach.call(context.querySelectorAll('[data-og-tabs]'), initRoot);
  }

  window.OnlyGamesTabs = {
    init: init,
    EVENT_NAME: EVENT_NAME,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})(window, document);
